/**
 * Loads a third-party script into the document, once.
 *
 * Both provider scripts arrive this way rather than through a bundle import, so
 * a fan who never opens a sign-in screen never downloads either of them.
 */

const scriptPromises = new Map<string, Promise<void>>();

/**
 * Injects a third-party script once per URL, per document.
 *
 * Memoized at module scope so a screen that mounts, unmounts, and mounts again
 * fetches nothing the second time. The promise is REMOVED from the cache on
 * failure, so a fan who reloads after a dropped connection gets a real retry
 * rather than a permanently poisoned rejection.
 */
export function loadExternalScript({ src }: { src: string }): Promise<void> {
  const cached = scriptPromises.get(src);
  if (cached) {
    return cached;
  }

  const pending = new Promise<void>((resolve, reject) => {
    // Another copy of this app, or a previous cache reset, may already have put
    // the tag in the document. Nothing would fire `onload` for it a second time.
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const element = document.createElement("script");
    element.src = src;
    element.async = true;
    element.onload = () => {
      resolve();
    };
    element.onerror = () => {
      reject(new Error(`Could not load ${src}`));
    };
    document.head.appendChild(element);
  });

  // Drop a rejected load from the cache so the next caller retries for real.
  // Keeping it would mean one flaky network moment permanently hides the
  // provider button for the life of the document.
  const memoized = pending.catch((cause: unknown) => {
    scriptPromises.delete(src);
    throw cause;
  });

  scriptPromises.set(src, memoized);
  return memoized;
}

/** Clears the memo. Tests only. */
export function __resetScriptCacheForTests(): void {
  scriptPromises.clear();
}
